/**
 * Claude Auth Panel
 *
 * Allows users to connect/disconnect their Claude account.
 * Uses an embedded terminal for the interactive auth flow:
 * 1. User clicks "Connect Claude Account"
 * 2. User runs `claude login` in the terminal
 * 3. User completes OAuth flow, pastes code in terminal
 * 4. Terminal detects success, refreshes status
 */

import { useState, useEffect, useCallback } from 'react';
import { api, type ClaudeAuthStatus } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ClaudeAuthTerminal, type TerminalStage } from './ClaudeAuthTerminal';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Link2,
  ChevronRight,
} from 'lucide-react';

type AuthWizardState = 'idle' | 'connecting' | 'error';

interface ClaudeAuthPanelProps {
  /** Callback when wizard state changes (for parent to adjust layout) */
  onWizardStateChange?: (isWizardActive: boolean) => void;
}

export function ClaudeAuthPanel({ onWizardStateChange }: ClaudeAuthPanelProps) {
  const { setClaudeConnected } = useAuthStore();
  const [status, setStatus] = useState<ClaudeAuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  // Wizard state
  const [wizardState, setWizardState] = useState<AuthWizardState>('idle');
  const [terminalStage, setTerminalStage] = useState<TerminalStage>('waiting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.claude.getStatus();
      setStatus(response);
    } catch (err) {
      console.error('Failed to fetch Claude status:', err);
      setStatus({
        cliInstalled: false,
        authenticated: false,
        error: 'Failed to check status',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Notify parent when wizard state changes
  useEffect(() => {
    onWizardStateChange?.(wizardState === 'connecting');
  }, [wizardState, onWizardStateChange]);

  const handleConnect = () => {
    setWizardState('connecting');
    setTerminalStage('waiting');
    setErrorMessage(null);
  };

  const handleCancel = () => {
    setWizardState('idle');
    setTerminalStage('waiting');
    setErrorMessage(null);
  };

  const handleStageChange = useCallback((stage: TerminalStage) => {
    setTerminalStage(stage);
  }, []);

  const handleAuthSuccess = async () => {
    console.log('[ClaudeAuthPanel] handleAuthSuccess called!');
    await fetchStatus();
    console.log('[ClaudeAuthPanel] Setting wizardState to idle...');
    setWizardState('idle');
    // Update auth store
    setClaudeConnected(true);
    // Notify other components (like ClaudeStatusIndicator) that auth changed
    window.dispatchEvent(new CustomEvent('claude-auth-changed'));
    console.log('[ClaudeAuthPanel] Auth success complete');
  };

  const handleRetry = () => {
    setErrorMessage(null);
    handleConnect();
  };

  const handleDisconnectClick = () => {
    setShowDisconnectConfirm(true);
  };

  const handleDisconnectConfirm = async () => {
    setShowDisconnectConfirm(false);
    setDisconnecting(true);
    try {
      await api.claude.disconnect();
      await fetchStatus();
      // Update auth store
      setClaudeConnected(false);
      // Clear TelcoOS localStorage data
      localStorage.removeItem('telcoos-settings');
      // Notify other components (like ClaudeStatusIndicator) that auth changed
      window.dispatchEvent(new CustomEvent('claude-auth-changed'));
    } catch (err) {
      console.error('Disconnect error:', err);
    } finally {
      setDisconnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Checking Claude status...</span>
      </div>
    );
  }

  // CLI not installed
  if (!status?.cliInstalled) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-destructive mt-0.5" />
          <div>
            <h4 className="font-medium text-destructive">Claude CLI Not Installed</h4>
            <p className="text-sm text-destructive/80 mt-1">
              The Claude CLI is not installed on the server. Contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Connected state
  if (status?.authenticated && wizardState === 'idle') {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-500/30 dark:bg-green-500/10">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-green-800 dark:text-green-200">Claude Connected</h4>
              {status.accountEmail && (
                <p className="text-sm text-green-700 dark:text-green-200/80 mt-1">
                  Signed in as: {status.accountEmail}
                </p>
              )}
              <p className="text-xs text-green-700/80 dark:text-green-200/60 mt-2">
                Your Claude account is connected. You can chat with Claude and run skills.
              </p>
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleDisconnectClick}
          disabled={disconnecting}
          className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
        >
          {disconnecting ? 'Disconnecting...' : 'Disconnect Account'}
        </Button>

        <ConfirmDialog
          isOpen={showDisconnectConfirm}
          onClose={() => setShowDisconnectConfirm(false)}
          onConfirm={handleDisconnectConfirm}
          title="Disconnect Claude Account"
          message="Are you sure you want to disconnect your Claude account? You'll need to reconnect to use Claude features."
          confirmLabel="Disconnect"
          variant="destructive"
        />
      </div>
    );
  }

  // Wizard: Connecting state - terminal with reactive instruction card
  if (wizardState === 'connecting') {
    // Instructions based on detected terminal stage
    const kbd = "px-1.5 py-0.5 bg-black text-white rounded font-mono text-xs";
    const stageInstructions: Record<TerminalStage, { title: string; description: React.ReactNode; hint: string }> = {
      waiting: {
        title: 'Run Login Command',
        description: <>Type <code className={kbd}>claude login</code> and press <code className={kbd}>ENTER</code>.</>,
        hint: '',
      },
      'login-method': {
        title: 'Select Login Method',
        description: <>Press <code className={kbd}>1</code> for Claude subscription (Pro, Max, Team, or Enterprise).</>,
        hint: '',
      },
      'theme-selection': {
        title: 'Skip Theme Selection',
        description: <>Press <code className={kbd}>ENTER</code> to skip.<br /><span className="text-muted-foreground">The app has its own theme settings.</span></>,
        hint: '',
      },
      'browser-open': {
        title: 'Complete Browser Auth',
        description: <>A browser window should have opened.<br /><span className="text-muted-foreground">Complete the login there, then return here.</span></>,
        hint: '',
      },
      'copy-url': {
        title: 'Copy the URL',
        description: <>Select the URL in the terminal and open it in your browser.<br /><span className="text-muted-foreground">After authorizing, you'll get a code to paste back here.</span></>,
        hint: '',
      },
      'paste-code': {
        title: 'Paste Auth Code',
        description: <>Paste the code from your browser and press <code className={kbd}>ENTER</code>.</>,
        hint: '',
      },
      'login-complete': {
        title: 'Login Complete',
        description: <>You're logged in! Press <code className={kbd}>ENTER</code> to continue.</>,
        hint: '',
      },
      'security-notes': {
        title: 'Security Notes',
        description: <>Press <code className={kbd}>ENTER</code> to continue.</>,
        hint: '',
      },
      'terminal-setup': {
        title: 'Terminal Setup',
        description: <>Press <code className={kbd}>1</code> and hit <code className={kbd}>ENTER</code>.<br /><span className="text-muted-foreground">TelcoOS handles terminal settings automatically.</span></>,
        hint: '',
      },
      'trust-folder': {
        title: 'Trust Folder',
        description: <>Press <code className={kbd}>ENTER</code> to trust this folder.</>,
        hint: '',
      },
      success: {
        title: 'Success!',
        description: <>You're now connected to Claude.</>,
        hint: '',
      },
      error: {
        title: 'Something Went Wrong',
        description: <>An error occurred. Click Cancel to start over.</>,
        hint: '',
      },
    };

    const currentInstruction = stageInstructions[terminalStage];

    // Stage order for progress indicator (copy-url and browser-open are alternatives at the same step)
    const stageOrder: TerminalStage[] = ['waiting', 'login-method', 'theme-selection', 'copy-url', 'paste-code', 'login-complete', 'security-notes', 'terminal-setup', 'trust-folder', 'success'];
    // Map browser-open to same position as copy-url for progress display
    const displayStage = terminalStage === 'browser-open' ? 'copy-url' : terminalStage;
    const currentStageIndex = stageOrder.indexOf(displayStage);

    return (
      <div className="col-span-3 space-y-4">
        {/* Progress indicator */}
        <div className="flex items-center gap-1 text-xs">
          {stageOrder.slice(0, -1).map((stage, index) => {
            const isActive = index === currentStageIndex;
            const isComplete = index < currentStageIndex;
            return (
              <div key={stage} className="flex items-center gap-1">
                {index > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                <span className={`${isActive ? 'text-primary-700 dark:text-primary-300 font-medium' : isComplete ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                  {isComplete ? '✓' : `${index + 1}.`} {stageInstructions[stage].title.split(' ').slice(0, 2).join(' ')}
                </span>
              </div>
            );
          })}
        </div>

        {/* Stacked layout: instruction card above terminal */}
        <div className="flex flex-col gap-4">
          <div className="w-full">
              <div className={`rounded-lg border p-4 ${
                terminalStage === 'success'
                  ? 'border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/10'
                  : terminalStage === 'error'
                  ? 'border-red-200 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10'
                  : 'border-primary-200 bg-primary-50 dark:border-primary-500/30 dark:bg-primary-500/10'
              }`}>
                <div className="flex items-start gap-3">
                  <Link2 className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                    terminalStage === 'success' ? 'text-green-600 dark:text-green-400' : terminalStage === 'error' ? 'text-red-600 dark:text-red-400' : 'text-primary-600 dark:text-primary-400'
                  }`} />
                  <div className="flex-1 space-y-3">
                    <h4 className={`font-medium ${
                      terminalStage === 'success' ? 'text-green-800 dark:text-green-200' : terminalStage === 'error' ? 'text-red-800 dark:text-red-200' : 'text-primary-800 dark:text-primary-200'
                    }`}>{currentInstruction.title}</h4>
                    <div className={`text-sm ${
                      terminalStage === 'success' ? 'text-green-700 dark:text-green-200/70' : terminalStage === 'error' ? 'text-red-700 dark:text-red-200/70' : 'text-primary-700 dark:text-primary-200/70'
                    }`}>{currentInstruction.description}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full">
            <ClaudeAuthTerminal
              onSuccess={handleAuthSuccess}
              onCancel={handleCancel}
              onStageChange={handleStageChange}
            />
          </div>
        </div>
      </div>
    );
  }

  // Wizard: Error
  if (wizardState === 'error') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 space-y-4 dark:border-red-500/30 dark:bg-red-500/10">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-red-800 dark:text-red-200">Authentication Failed</h4>
            <p className="text-sm text-red-700 dark:text-red-200/70 mt-1">
              {errorMessage || 'An error occurred during authentication.'}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="ghost"
            onClick={handleCancel}
            className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:text-red-200/70 dark:hover:bg-red-500/10 dark:hover:text-red-200"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRetry}
            variant="outline"
            className="flex-1 border-red-200 text-red-600 hover:bg-red-100 dark:border-red-500/30 dark:text-red-200 dark:hover:bg-red-500/10"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // Not connected (idle state)
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-medium text-amber-800 dark:text-amber-200">Claude Not Connected</h4>
            <p className="text-sm text-amber-700 dark:text-amber-200/80 mt-1">
              Connect your Claude account to start chatting and running skills.
            </p>
            {status?.error && (
              <p className="text-xs text-amber-700/80 dark:text-amber-200/60 mt-2">
                {status.error}
              </p>
            )}
          </div>
        </div>
      </div>

      <Button onClick={handleConnect}>
        Connect Claude Account
      </Button>
    </div>
  );
}

export default ClaudeAuthPanel;
